import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  CheckIcon,
  XMarkIcon,
  ShieldCheckIcon,
  ChartBarSquareIcon,
  CubeIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { PublicLayout } from '../components/layouts';
import { ProductLine } from '../types/auth.types';
import { usePricing } from '../hooks/usePricing';
import { Seo } from '../seo/Seo';
import {
  DeckHero,
  DeckSection,
  DeckSectionHeader,
  DeckPricingGrid,
  DeckFaqList,
  DeckFinalCta,
  useDeckAnimateOnScroll,
  type DeckPricingTier,
} from '../components/landing/deck';

const Pricing: React.FC = () => {
  const { t } = useTranslation('pricing');
  const [searchParams, setSearchParams] = useSearchParams();
  const productParam = searchParams.get('product') as ProductLine | null;
  const { pricing } = usePricing();
  useDeckAnimateOnScroll();

  const [selectedProduct, setSelectedProduct] = useState<ProductLine | null>(
    productParam && ['comply', 'quality', 'full'].includes(productParam) ? productParam : null
  );

  // Scroll to top only when the user lands on /pricing via a deep link
  // (e.g. from the home page's product cards: /pricing?product=quality).
  // Do NOT re-fire on subsequent in-page product-filter changes — that
  // was yanking users back to the top while they were reading the
  // comparison table.
  useEffect(() => {
    if (productParam) window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedProduct) setSearchParams({ product: selectedProduct });
    else setSearchParams({});
  }, [selectedProduct, setSearchParams]);

  const buildTier = (
    id: 'free' | 'starter' | 'growth' | 'enterprise',
    price: React.ReactNode,
    priceCaption: string | undefined,
    featureKeys: string[],
    notIncluded: string[] | undefined,
    productSlug: string | undefined
  ): DeckPricingTier => ({
    name: t(`tiers.${id}.name`),
    price,
    priceCaption,
    description: t(`tiers.${id}.description`),
    badge: id === 'starter' ? t('tiers.starter.badge') : undefined,
    highlight: id === 'starter',
    features: featureKeys.map((k) => t(`tiers.${id}.features.${k}`)),
    notIncluded: notIncluded?.map((k) => t(`tiers.${id}.limitations.${k}`)),
    cta: {
      label: t(`tiers.${id}.cta`),
      to:
        id === 'enterprise'
          ? '/contact'
          : productSlug
          ? `/signup?product=${productSlug}`
          : '/signup',
    },
  });

  const renderPrice = (amount: number | null) => {
    if (amount === null) return t('tiers.enterprise.price');
    if (amount === 0) return t('card.free');
    return (
      <span>
        ${amount}
        <span className="text-base text-white/50 font-light">{t('card.perMonth')}</span>
      </span>
    );
  };

  // Per-product tier sets
  const productTiers: Record<ProductLine, DeckPricingTier[]> = useMemo(() => ({
    comply: [
      buildTier(
        'free',
        renderPrice(0),
        undefined,
        ['minutes', 'transcription', 'dashboard', 'support', 'retention'],
        ['sentiment', 'integrations', 'api'],
        'comply'
      ),
      buildTier(
        'starter',
        renderPrice(Math.round(pricing.comply.starter.amount)),
        '$0.12/min en exceso',
        ['minutes', 'compliance', 'sentiment', 'support', 'retention', 'webhooks'],
        ['models', 'manager'],
        'comply'
      ),
      buildTier(
        'growth',
        renderPrice(Math.round(pricing.comply.growth.amount)),
        '$0.10/min en exceso',
        ['minutes', 'compliance', 'support', 'retention', 'integrations', 'api', 'rules', 'onboarding'],
        ['models', 'infrastructure'],
        'comply'
      ),
    ],
    quality: [
      buildTier(
        'free',
        renderPrice(0),
        undefined,
        ['minutes', 'transcription', 'dashboard', 'support', 'retention'],
        ['sentiment', 'integrations', 'api'],
        'quality'
      ),
      buildTier(
        'starter',
        renderPrice(Math.round(pricing.quality.starter.amount)),
        '$0.09/min en exceso',
        ['minutes', 'compliance', 'sentiment', 'support', 'retention', 'team'],
        ['models', 'manager'],
        'quality'
      ),
      buildTier(
        'growth',
        renderPrice(Math.round(pricing.quality.growth.amount)),
        '$0.085/min en exceso',
        ['minutes', 'compliance', 'support', 'retention', 'api', 'rules', 'coaching', 'onboarding'],
        ['models', 'infrastructure'],
        'quality'
      ),
    ],
    full: [
      buildTier(
        'free',
        renderPrice(0),
        undefined,
        ['minutes', 'transcription', 'dashboard', 'support', 'retention'],
        ['api', 'integrations'],
        'full'
      ),
      buildTier(
        'starter',
        renderPrice(Math.round(pricing.full.starter.amount)),
        '$0.14/min en exceso',
        ['minutes', 'compliance', 'sentiment', 'support', 'retention', 'webhooks', 'reports', 'team'],
        ['models', 'manager'],
        'full'
      ),
      buildTier(
        'growth',
        renderPrice(Math.round(pricing.full.growth.amount)),
        '$0.12/min en exceso',
        ['minutes', 'compliance', 'support', 'retention', 'integrations', 'api', 'rules', 'coaching', 'onboarding'],
        ['models', 'infrastructure'],
        'full'
      ),
    ],
  }), [pricing, t]);

  // Unified view (all products)
  const unifiedTiers: DeckPricingTier[] = useMemo(() => [
    buildTier(
      'free',
      renderPrice(0),
      undefined,
      ['minutes', 'transcription', 'dashboard', 'support', 'retention', 'reports'],
      ['sentiment', 'integrations', 'api', 'support'],
      undefined
    ),
    buildTier(
      'starter',
      renderPrice(Math.round(pricing.quality.starter.amount)),
      undefined,
      ['minutes', 'transcription', 'compliance', 'sentiment', 'support', 'retention', 'webhooks', 'reports', 'team'],
      ['models', 'manager', 'sla'],
      undefined
    ),
    buildTier(
      'growth',
      renderPrice(Math.round(pricing.quality.growth.amount)),
      undefined,
      ['minutes', 'transcription', 'compliance', 'sentiment', 'support', 'retention', 'integrations', 'api', 'rules', 'team', 'coaching', 'onboarding'],
      ['models', 'infrastructure'],
      undefined
    ),
    buildTier(
      'enterprise',
      renderPrice(null),
      undefined,
      ['calls', 'transcription', 'analytics', 'models', 'support', 'retention', 'integrations', 'api', 'sla', 'security', 'infrastructure', 'training', 'manager', 'discounts'],
      [],
      undefined
    ),
  ], [pricing, t]);

  const tiersToShow = selectedProduct ? productTiers[selectedProduct] : unifiedTiers;

  const featuresComparison = [
    { key: 'minutes', free: '500', starter: '1,000', growth: '5,000', enterprise: t('comparison.values.unlimited') },
    { key: 'languages', free: t('comparison.values.15plus'), starter: t('comparison.values.15plus'), growth: t('comparison.values.15plus'), enterprise: t('comparison.values.15plusCustom') },
    { key: 'compliance', free: t('comparison.values.basic'), starter: t('comparison.values.16point'), growth: t('comparison.values.16point'), enterprise: t('comparison.values.custom') },
    { key: 'sentiment', free: false, starter: true, growth: true, enterprise: true },
    { key: 'webhooks', free: false, starter: true, growth: true, enterprise: true },
    { key: 'coaching', free: false, starter: false, growth: true, enterprise: true },
    { key: 'api', free: false, starter: false, growth: true, enterprise: true },
    { key: 'rules', free: false, starter: false, growth: true, enterprise: true },
    { key: 'retention', free: t('comparison.values.7days'), starter: t('comparison.values.30days'), growth: t('comparison.values.90days'), enterprise: t('comparison.values.unlimited') },
    { key: 'support', free: t('comparison.values.community'), starter: t('comparison.values.email24h'), growth: t('comparison.values.priority4h'), enterprise: t('comparison.values.dedicatedTeam') },
    { key: 'sla', free: false, starter: false, growth: false, enterprise: true },
    { key: 'customAi', free: false, starter: false, growth: false, enterprise: true },
  ] as const;

  const renderCell = (val: boolean | string) => {
    if (typeof val === 'boolean') {
      return val ? (
        <CheckIcon className="w-4 h-4 text-sky-400 mx-auto" />
      ) : (
        <XMarkIcon className="w-4 h-4 text-white/20 mx-auto" />
      );
    }
    return <span>{val}</span>;
  };

  const faqItems = (['trial', 'exceed', 'changePlans', 'payment', 'languages', 'accuracy', 'refunds', 'security', 'customPlan'] as const).map(
    (k) => ({
      question: t(`faq.questions.${k}.question`),
      answer: t(`faq.questions.${k}.answer`),
    })
  );

  const productOptions = [
    { id: null, label: t('selector.all'), tag: undefined, icon: CubeIcon },
    { id: 'comply' as ProductLine, label: t('selector.comply'), tag: t('selector.complyTag'), icon: ShieldCheckIcon },
    { id: 'quality' as ProductLine, label: t('selector.quality'), tag: t('selector.qualityTag'), icon: ChartBarSquareIcon },
    { id: 'full' as ProductLine, label: t('selector.full'), tag: t('selector.fullTag'), icon: SparklesIcon },
  ];

  return (
    <PublicLayout transparentHeader>
      <Seo route="/pricing" includeSoftwareApplication />

      <DeckHero
        title={t('hero.title')}
        subtitle={t('hero.subtitle')}
        primaryCta={{ label: t('cta.startTrial'), to: '/contact' }}
        secondaryCta={{ label: t('cta.talkToSales'), to: '/contact' }}
        announcement={[
          t('hero.features.trial'),
          t('hero.features.noCard'),
          t('hero.features.cancel'),
        ].join(' · ')}
      />

      <DeckSection size="compact">
        <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-6 text-center">
          {t('selector.eyebrow')}
        </p>
        <div className="flex flex-wrap justify-center gap-3 mb-12 animate-on-scroll">
          {productOptions.map(({ id, label, tag, icon: Icon }) => {
            const active = selectedProduct === id;
            return (
              <button
                key={String(id)}
                onClick={() => setSelectedProduct(id)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium border transition-colors ${
                  active
                    ? 'border-sky-500 bg-sky-500/10 text-white'
                    : 'border-white/15 text-white/70 hover:border-white/30 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
                {tag && <span className="text-xs text-white/60">· {tag}</span>}
              </button>
            );
          })}
        </div>
        <DeckPricingGrid
          tiers={tiersToShow}
          includeLabel={t('card.includes')}
          notIncludeLabel={t('card.notIncluded')}
        />
      </DeckSection>

      <DeckSection id="comparison">
        <DeckSectionHeader title={t('comparison.title')} subtitle={t('comparison.subtitle')} />
        <div className="overflow-x-auto animate-on-scroll">
          <table className="w-full max-w-6xl">
            <thead>
              <tr className="border-b border-white/10">
                <th className="px-4 py-4 text-left text-xs uppercase tracking-[0.15em] text-white/60 font-medium">
                  {t('comparison.headers.feature')}
                </th>
                <th className="px-4 py-4 text-center text-xs uppercase tracking-[0.15em] text-white/60 font-medium">
                  {t('comparison.headers.free')}
                </th>
                <th className="px-4 py-4 text-center text-xs uppercase tracking-[0.15em] text-sky-400 font-medium">
                  {t('comparison.headers.starter')}
                </th>
                <th className="px-4 py-4 text-center text-xs uppercase tracking-[0.15em] text-white/60 font-medium">
                  {t('comparison.headers.growth')}
                </th>
                <th className="px-4 py-4 text-center text-xs uppercase tracking-[0.15em] text-white/60 font-medium">
                  {t('comparison.headers.enterprise')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {featuresComparison.map((row) => (
                <tr key={row.key}>
                  <td className="px-4 py-4 text-sm text-white/80">
                    {t(`comparison.features.${row.key}`)}
                  </td>
                  <td className="px-4 py-4 text-center text-sm text-white/60">{renderCell(row.free)}</td>
                  <td className="px-4 py-4 text-center text-sm text-white">{renderCell(row.starter)}</td>
                  <td className="px-4 py-4 text-center text-sm text-white/60">{renderCell(row.growth)}</td>
                  <td className="px-4 py-4 text-center text-sm text-white/60">{renderCell(row.enterprise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DeckSection>

      <DeckSection>
        <DeckSectionHeader badge={t('faq.eyebrow')} title={t('faq.title')} />
        <DeckFaqList items={faqItems} />
        <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-4 animate-on-scroll">
          <p className="text-base text-white/60">{t('faq.stillQuestions')}</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 text-base font-medium transition-colors"
          >
            {t('faq.contactSales')} →
          </Link>
          <Link
            to="/how-we-evaluate"
            className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 text-base font-medium transition-colors"
          >
            {t('faq.howWeEvaluate', { defaultValue: 'See exactly what we evaluate' })} →
          </Link>
        </div>
      </DeckSection>

      <DeckFinalCta
        title={t('cta.title')}
        subtitle={t('cta.subtitle')}
        email="ventas@callcov.com"
        cta={{ label: t('cta.startTrial'), to: '/contact' }}
      />
    </PublicLayout>
  );
};

export default Pricing;
